import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { cn } from '@siliconx/shared/ui/utils';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import {
  describeChange,
  sourceLabel,
  type EditableField,
  type Proposal,
  type ReviewRow,
} from '../../lib/importReview';

type Filter = 'all' | 'errors' | 'excluded';
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All rows' },
  { value: 'errors', label: 'Needs fixes' },
  { value: 'excluded', label: 'Excluded' },
];

const PAGE = 100;

/** Cells an org admin can edit (the apply-fix allowlist; manufacturer/model come from the mapping). */
const EDIT_COLUMNS: { key: EditableField; label: string; get: (r: ReviewRow) => string | null }[] = [
  { key: 'seller_sku_raw', label: 'Part number', get: (r) => r.sellerSku },
  { key: 'serial_number', label: 'Serial', get: (r) => r.serialNumber },
  { key: 'quantity', label: 'Qty', get: (r) => String(r.quantity) },
  { key: 'site', label: 'Site', get: (r) => r.site },
  { key: 'condition_band', label: 'Condition', get: (r) => r.conditionBand },
  { key: 'asset_tag', label: 'Asset tag', get: (r) => r.assetTag },
];

/**
 * SIL-1617 - editable row table. Cells commit through PATCH rows/:rowId
 * (apply-fix, source = 'user'); pending proposals show inline as before -> after.
 */
export function RowTable({
  batchId,
  canEdit,
  busy,
  proposalsByRow,
  focusRows,
  onClearFocus,
  onEdit,
  onApply,
  onDismiss,
}: {
  batchId: string;
  canEdit: boolean;
  busy: boolean;
  proposalsByRow: Map<number, Proposal[]>;
  /** Row numbers of the fix card being reviewed; null = no focus. */
  focusRows: Set<number> | null;
  onClearFocus: () => void;
  onEdit: (row: ReviewRow, field: EditableField, value: string | null) => Promise<unknown>;
  onApply: (proposalId: string) => Promise<unknown>;
  onDismiss: (proposalId: string) => Promise<unknown>;
}) {
  const [filter, setFilter] = useState<Filter>('all');

  const q = useInfiniteQuery({
    queryKey: qk.importBatch.rows(batchId, filter),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const res = await api.api.client.import.batches[':batchId'].rows.$get({
        param: { batchId },
        query: { filter, limit: String(PAGE), ...(pageParam > 0 ? { after: String(pageParam) } : {}) },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as unknown as { rows: ReviewRow[]; nextAfter: number | null };
    },
    getNextPageParam: (last) => last.nextAfter ?? undefined,
  });

  // Reviewing a card needs every row it touches: drain the pages while focused.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  useEffect(() => {
    if (focusRows && hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [focusRows, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const all = q.data?.pages.flatMap((p) => p.rows) ?? [];
  const rows = focusRows ? all.filter((r) => r.rowNum !== null && focusRows.has(r.rowNum)) : all;

  return (
    <Card as="section" aria-label="Rows" padding="md" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">Rows</h2>
        <div className="flex flex-wrap items-center gap-2">
          {focusRows && (
            <Button size="sm" variant="secondary" onClick={onClearFocus}>
              Show all rows
            </Button>
          )}
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset',
                filter === f.value ? 'bg-brand-600 text-white ring-brand-600' : 'bg-card text-muted-foreground ring-border hover:bg-muted',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading rows...</p>}
      {q.error && (
        <p role="alert" className="text-sm text-status-red">
          {(q.error as Error).message}
        </p>
      )}
      {!q.isLoading && rows.length === 0 && <p className="text-sm text-muted-foreground">No rows to show.</p>}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm" data-testid="row-table">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <th className="py-1.5 pr-3">Row</th>
                <th className="py-1.5 pr-3">Manufacturer</th>
                <th className="py-1.5 pr-3">Model</th>
                {EDIT_COLUMNS.map((c) => (
                  <th key={c.key} className="py-1.5 pr-3">
                    {c.label}
                  </th>
                ))}
                <th className="py-1.5 pr-3">Status</th>
                {canEdit && <th className="py-1.5" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <RowLines
                  key={r.id}
                  row={r}
                  canEdit={canEdit}
                  busy={busy}
                  proposals={r.rowNum !== null ? (proposalsByRow.get(r.rowNum) ?? []) : []}
                  onEdit={onEdit}
                  onApply={onApply}
                  onDismiss={onDismiss}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!focusRows && hasNextPage && (
        <div className="flex justify-center">
          <Button size="sm" variant="secondary" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
            {isFetchingNextPage ? 'Loading...' : 'Load more rows'}
          </Button>
        </div>
      )}
    </Card>
  );
}

function RowLines({
  row,
  canEdit,
  busy,
  proposals,
  onEdit,
  onApply,
  onDismiss,
}: {
  row: ReviewRow;
  canEdit: boolean;
  busy: boolean;
  proposals: Proposal[];
  onEdit: (row: ReviewRow, field: EditableField, value: string | null) => Promise<unknown>;
  onApply: (proposalId: string) => Promise<unknown>;
  onDismiss: (proposalId: string) => Promise<unknown>;
}) {
  const tone = row.excluded ? 'neutral' : row.isValid ? 'green' : 'red';
  const label = row.excluded ? 'Excluded' : row.isValid ? 'Accepted' : 'Needs fix';
  const extras = row.rowErrors.length > 0 || proposals.length > 0 || row.parentRowNum !== null;

  return (
    <>
      <tr
        data-testid="import-row"
        data-row={row.rowNum}
        data-excluded={row.excluded}
        data-parent={row.parentRowNum ?? ''}
        data-quantity={row.quantity}
        className={cn(row.excluded && 'text-muted-foreground')}
      >
        <td className="py-2 pr-3 tabular-nums">{row.rowNum}</td>
        <td className="py-2 pr-3">{row.manufacturer ?? ''}</td>
        <td className="max-w-[18rem] truncate py-2 pr-3" title={row.model ?? ''}>
          {row.model ?? ''}
        </td>
        {EDIT_COLUMNS.map((c) => (
          <td key={c.key} className="py-2 pr-3">
            <EditableCell row={row} column={c} canEdit={canEdit} busy={busy} onEdit={onEdit} />
          </td>
        ))}
        <td className="py-2 pr-3">
          <StatusPill tone={tone}>{label}</StatusPill>
        </td>
        {canEdit && (
          <td className="py-2 text-right">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              aria-label={`${row.excluded ? 'Include' : 'Exclude'} row ${row.rowNum}`}
              onClick={() => void onEdit(row, 'exclude_row', row.excluded ? 'false' : 'true')}
            >
              {row.excluded ? 'Include' : 'Exclude'}
            </Button>
          </td>
        )}
      </tr>
      {extras && (
        <tr>
          <td />
          <td colSpan={EDIT_COLUMNS.length + 3 + (canEdit ? 1 : 0) - 1} className="pb-2 pr-3 text-xs">
            {row.parentRowNum !== null && <p className="text-muted-foreground">Attached to row {row.parentRowNum}</p>}
            {row.rowErrors.map((e) => (
              <p key={`${e.field}-${e.code}`} className="text-status-red">
                <span className="font-mono">{e.code}</span> - {e.message}
              </p>
            ))}
            {proposals.map((p) => (
              <div key={p.id} data-testid="inline-proposal" className="mt-1 flex flex-wrap items-center gap-2 text-foreground">
                <StatusPill tone="neutral">{sourceLabel(p.source)}</StatusPill>
                <span>{p.changes.map(describeChange).join('; ')}</span>
                {p.reason && <span className="text-muted-foreground">({p.reason})</span>}
                {canEdit && (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => void onApply(p.id)}>
                      Apply
                    </Button>
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => void onDismiss(p.id)}>
                      Dismiss
                    </Button>
                  </>
                )}
              </div>
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

function EditableCell({
  row,
  column,
  canEdit,
  busy,
  onEdit,
}: {
  row: ReviewRow;
  column: (typeof EDIT_COLUMNS)[number];
  canEdit: boolean;
  busy: boolean;
  onEdit: (row: ReviewRow, field: EditableField, value: string | null) => Promise<unknown>;
}) {
  const value = column.get(row);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const doneRef = useRef(false);

  if (!canEdit) return <span>{value ?? ''}</span>;

  if (!editing) {
    return (
      <button
        type="button"
        aria-label={`Edit ${column.label} for row ${row.rowNum}`}
        disabled={busy || row.excluded}
        onClick={() => {
          setDraft(value ?? '');
          doneRef.current = false;
          setEditing(true);
        }}
        className="min-h-6 min-w-12 rounded px-1 text-left hover:bg-muted disabled:cursor-not-allowed"
      >
        {value ?? <span className="text-muted-foreground">add</span>}
      </button>
    );
  }

  const commit = () => {
    if (doneRef.current) return; // Enter then the blur that follows must not double-submit
    doneRef.current = true;
    setEditing(false);
    const next = draft.trim() === '' ? null : draft.trim();
    if (next !== (value === '' ? null : value)) void onEdit(row, column.key, next);
  };

  return (
    <input
      autoFocus
      aria-label={`${column.label} for row ${row.rowNum}`}
      value={draft}
      maxLength={200}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') {
          e.preventDefault();
          doneRef.current = true;
          setEditing(false);
        }
      }}
      className="w-28 rounded-md border border-brand-500 px-1.5 py-0.5 text-sm"
    />
  );
}
